import dayjs from 'dayjs';

import { PrintScheduleMatch, Timezone, PrintSchedule, PrintScheduleDate } from '../../types';

// Helper function to get filtered courts from sessions
const getFilteredCourtsFromSessions = (date: PrintScheduleDate) => {
	return date.sessions.flatMap((session) => session.courts).filter((court) => court.matches.length > 0);
};

const MatchBox = ({ match, timezone }: { match: PrintScheduleMatch; timezone?: Timezone | undefined }) => {
	const {
		podName,
		matchName,
		estStartTime,
		teamOneAbbreviation,
		teamTwoAbbreviation,
		teamOnePlayerOneFirstName,
		teamOnePlayerOneLastName,
		teamOnePlayerTwoFirstName,
		teamOnePlayerTwoLastName,
		teamTwoPlayerOneFirstName,
		teamTwoPlayerOneLastName,
		teamTwoPlayerTwoFirstName,
		teamTwoPlayerTwoLastName
	} = match;

	let formattedTime = '';
	try {
		formattedTime = dayjs.utc(estStartTime).format('ddd hh:mm A');
	} catch {
		formattedTime = estStartTime;
	}

	const getPlayerName = (firstName: string | null, lastName: string | null) => {
		if (!firstName && !lastName) return null;
		return `${firstName || ''} ${lastName || ''}`.trim();
	};

	const teamOnePlayerOne = getPlayerName(teamOnePlayerOneFirstName, teamOnePlayerOneLastName);
	const teamOnePlayerTwo = getPlayerName(teamOnePlayerTwoFirstName, teamOnePlayerTwoLastName);
	const teamTwoPlayerOne = getPlayerName(teamTwoPlayerOneFirstName, teamTwoPlayerOneLastName);
	const teamTwoPlayerTwo = getPlayerName(teamTwoPlayerTwoFirstName, teamTwoPlayerTwoLastName);

	return (
		<div className="match-box border border-gray-300 rounded-lg px-2 py-1 bg-white text-[12px] mr-1 mb-1">
			<div className="font-bold text-center text-xs">
				{podName ? <span className="text-gray-500">{podName} - </span> : null}
				{matchName}
			</div>
			<div className="text-xs text-gray-600 mb-1 text-center text-[10px]">
				{formattedTime} ({timezone?.abbreviation})
			</div>
			<div className="flex flex-col gap-1">
				{/* Team One - Away */}
				<div className="flex  w-full items-center">
					<div className="font-bold mr-1 min-w-[68px] justify-center text-xs">{teamTwoAbbreviation} (A)</div>
					<div className="flex flex-col text-xs">
						{teamTwoPlayerOne ? <span className="text-black">{teamTwoPlayerOne}</span> : null}
						{teamTwoPlayerTwo ? <span className="text-black">{teamTwoPlayerTwo}</span> : null}
						{!teamTwoPlayerOne && !teamTwoPlayerTwo && <span className="italic text-gray-400 justify-center">No players listed</span>}
					</div>
				</div>
				{/* Team Two - Home */}
				<div className="flex w-full items-center">
					<div className="font-bold mr-1 min-w-[68px] justify-center text-xs">{teamOneAbbreviation} (H)</div>
					<div className="flex flex-col text-xs">
						{teamOnePlayerOne ? <span className="text-black">{teamOnePlayerOne}</span> : null}
						{teamOnePlayerTwo ? <span className="text-black">{teamOnePlayerTwo}</span> : null}
						{!teamOnePlayerOne && !teamOnePlayerTwo && <span className="italic text-gray-400">No players listed</span>}
					</div>
				</div>
			</div>
		</div>
	);
};

export const ScheduleEventSheetPrint = ({
	schedule,
	dateIndex = 0,
	pageNumber = 0,
	sessionType = 'all'
}: {
	schedule: PrintSchedule;
	dateIndex?: number;
	pageNumber?: number;
	sessionType?: 'all' | 'morning' | 'afternoon';
}) => {
	const currentDate = schedule.dates[dateIndex];

	if (!currentDate) {
		return <div className="print-page">No date found for index {dateIndex}</div>;
	}

	const maxMatchesPerColumn = 5;
	const columnsPerPage = 4;

	// Create columns from all courts (courts can span multiple columns)
	const allColumns: Array<{ courtName: string; matches: PrintScheduleMatch[]; columnTitle: string }> = [];

	// Get all courts from all sessions and filter out courts with no matches
	const filteredCourts = getFilteredCourtsFromSessions(currentDate);

	const getSessionTypeLabel = () => {
		switch (sessionType) {
			case 'morning':
				return 'Morning Session';
			case 'afternoon':
				return 'Afternoon Session';
			default:
				return null;
		}
	};

	const sessionLabel = getSessionTypeLabel();

	// If no valid courts, show message
	if (filteredCourts.length === 0) {
		return (
			<div className="print-page min-h-screen w-full bg-white flex items-center justify-center">
				<div className="text-center text-gray-500">
					<h1 className="text-3xl font-bold mb-4">
						{schedule.eventName} - {dayjs.utc(currentDate.date).format('dddd, MMM D, YYYY')}
					</h1>
					{sessionLabel ? <h2 className="text-xl font-semibold text-orange-600 mb-4">{sessionLabel}</h2> : null}
					<p className="text-lg">No scheduled matches for this date</p>
				</div>
			</div>
		);
	}

	filteredCourts.forEach((court) => {
		// Split court matches into chunks of 5
		const matchChunks = [];
		for (let i = 0; i < court.matches.length; i += maxMatchesPerColumn) {
			matchChunks.push(court.matches.slice(i, i + maxMatchesPerColumn));
		}

		// If court has no matches, still create one column
		if (matchChunks.length === 0) {
			matchChunks.push([]);
		}

		// Create a column for each chunk
		matchChunks.forEach((matches, chunkIndex) => {
			allColumns.push({
				courtName: court.courtName,
				matches: matches,
				columnTitle: chunkIndex === 0 ? court.courtName : `${court.courtName} (cont.)`
			});
		});
	});

	// Get columns for current page
	const startColumnIndex = pageNumber * columnsPerPage;
	const endColumnIndex = startColumnIndex + columnsPerPage;
	const currentPageColumns = allColumns.slice(startColumnIndex, endColumnIndex);

	return (
		<div className="print-page min-h-screen w-full bg-white">
			<div className="text-center mb-3">
				<h1 className="text-3xl font-bold">
					{schedule.eventName} - {dayjs.utc(currentDate.date).format('dddd, MMM D, YYYY')}
				</h1>
				{sessionLabel ? <h2 className="text-xl font-semibold text-orange-600 mt-1">{sessionLabel}</h2> : null}
			</div>
			{/* Dynamic Column Grid Layout */}
			<div className="grid gap-1 h-full grid-cols-4 mx-20">
				{currentPageColumns.map((column, columnIndex) => (
					<div key={columnIndex} className="flex flex-col">
						<h3 className="font-bold text-gray-800 text-center">Court: {column.columnTitle}</h3>

						{/* Court Matches Container - 5 rows */}
						<div className="flex flex-col flex-1 min-h-[590px]">
							{Array.from({ length: maxMatchesPerColumn }, (_, rowIndex) => {
								const match = column.matches[rowIndex];
								return (
									<div key={rowIndex} className="flex-1 mb-1 last:mb-0">
										{match ? <MatchBox match={match} timezone={schedule.timezone} /> : null}
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Multi-page component that displays all dates with column wrapping support
export const ScheduleEventMultiPagePrint = ({
	schedule,
	sessionType = 'all'
}: {
	schedule: PrintSchedule;
	sessionType?: 'all' | 'morning' | 'afternoon';
}) => {
	const maxMatchesPerColumn = 5;
	const columnsPerPage = 4;

	// Generate all pages for all dates
	const allPages: { dateIndex: number; pageNumber: number; key: string }[] = [];

	schedule.dates.forEach((date, dateIndex) => {
		// Get all courts from all sessions and filter out courts with no matches
		const filteredCourts = getFilteredCourtsFromSessions(date);

		// Calculate total columns needed for this date
		let totalColumns = 0;

		filteredCourts.forEach((court) => {
			// Each court needs ceil(matches/5) columns
			const columnsNeeded = Math.max(1, Math.ceil(court.matches.length / maxMatchesPerColumn));
			totalColumns += columnsNeeded;
		});

		// Calculate how many pages we need for this date (4 columns per page)
		const totalPagesForDate = Math.max(1, Math.ceil(totalColumns / columnsPerPage));

		// Only add pages if there are courts with matches
		if (filteredCourts.length > 0) {
			// Add all pages for this date
			for (let pageNumber = 0; pageNumber < totalPagesForDate; pageNumber++) {
				allPages.push({
					dateIndex,
					pageNumber,
					key: `${dateIndex}-${pageNumber}`
				});
			}
		}
	});

	return (
		<div className="schedule-multi-page-print">
			<style>{`
				@media print {
					.print-page {
						min-height: 100%;
						width: 100%;
						page-break-inside: avoid;
						padding: 0;
                        margin: 0;
					}
					.match-box {
						break-inside: avoid;
						page-break-inside: avoid;
					}
					body {
						margin: 0;
						padding: 0;
					}
					@page {
						margin-top: 72px;
						padding: 0;
						size: A4 landscape;
					}
				}
			`}</style>

			{allPages.map((page) => (
				<ScheduleEventSheetPrint
					key={page.key}
					schedule={schedule}
					dateIndex={page.dateIndex}
					pageNumber={page.pageNumber}
					sessionType={sessionType}
				/>
			))}
		</div>
	);
};
